/**
 * Proveedores de imagen que puede enviar n8n (o admin).
 * Crédito público: pexels | dreamvinyl (IA: Pollinations, Models Lab, HF…) | other (manual).
 */
export const NOTICIA_IMAGEN_PROVIDER = {
  POLLINATIONS: 'pollinations',
  MODELS_LAB: 'models_lab',
  HUGGINGFACE: 'huggingface',
  PEXELS: 'pexels',
  DREAMVINYL: 'dreamvinyl',
  OTHER: 'other',
};

/** Modos de entrega de imagen en borrador. */
export const NOTICIA_IMAGEN_DELIVERY = {
  URL: 'url',
  BASE64: 'base64',
  NONE: 'none',
};

export function isPollinationsUrl(url) {
  return /pollinations\.ai/i.test(String(url ?? ''));
}

export function isModelsLabUrl(url) {
  return /modelslab\.com|models\.lab/i.test(String(url ?? ''));
}

export function isPexelsUrl(url) {
  return /pexels\.com/i.test(String(url ?? ''));
}

export function normalizeImagenProvider(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return null;
  if (s === 'pexels') return NOTICIA_IMAGEN_PROVIDER.PEXELS;
  if (s === 'pollinations' || s === 'pollination') return NOTICIA_IMAGEN_PROVIDER.POLLINATIONS;
  if (
    s === 'models_lab' ||
    s === 'modelslab' ||
    s === 'models-lab' ||
    s === 'z_image_turbo' ||
    s === 'z-image-turbo' ||
    s === 'zimageturbo'
  ) {
    return NOTICIA_IMAGEN_PROVIDER.MODELS_LAB;
  }
  if (s === 'huggingface' || s === 'hf') return NOTICIA_IMAGEN_PROVIDER.HUGGINGFACE;
  if (s === 'dreamvinyl' || s === 'dreamvinyl_ai' || s === 'upload' || s === 'ia' || s === 'ai') {
    return NOTICIA_IMAGEN_PROVIDER.DREAMVINYL;
  }
  if (s === 'other' || s === 'otra' || s === 'externa') return NOTICIA_IMAGEN_PROVIDER.OTHER;
  return null;
}

export function inferImagenProviderFromUrl(url) {
  const s = String(url ?? '').trim();
  if (!s || s.startsWith('data:')) return null;
  if (isPexelsUrl(s)) return NOTICIA_IMAGEN_PROVIDER.PEXELS;
  if (isPollinationsUrl(s)) return NOTICIA_IMAGEN_PROVIDER.POLLINATIONS;
  if (isModelsLabUrl(s)) return NOTICIA_IMAGEN_PROVIDER.MODELS_LAB;
  return null;
}

/**
 * @param {Record<string, unknown>|null|undefined} metadata
 * @param {string|null|undefined} imagenUrl
 * @returns {string|null}
 */
export function resolveNoticiaImagenProvider(metadata, imagenUrl) {
  const base = metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};
  const imagen =
    base.imagen && typeof base.imagen === 'object' && !Array.isArray(base.imagen) ? base.imagen : {};

  const fromMeta = normalizeImagenProvider(
    imagen.provider ??
      imagen.imagen_provider ??
      base.imagen_provider ??
      base.imagen_source ??
      base.image_source ??
      base.image_provider ??
      imagen.source
  );
  if (fromMeta) return fromMeta;

  return inferImagenProviderFromUrl(imagenUrl);
}

/**
 * @param {{ imagen_url?: string|null, metadata?: object|null }} row
 * @returns {'url'|'base64'|'none'}
 */
export function resolveNoticiaImagenDelivery(row) {
  const url = String(row?.imagen_url ?? '').trim();
  if (url) return NOTICIA_IMAGEN_DELIVERY.URL;
  const base = row?.metadata && typeof row.metadata === 'object' ? row.metadata : {};
  const imagen = base.imagen && typeof base.imagen === 'object' ? base.imagen : {};
  if (base.imagen_base64 || base.imagenBase64 || imagen.imagen_base64 || imagen.imagenBase64) {
    return NOTICIA_IMAGEN_DELIVERY.BASE64;
  }
  return NOTICIA_IMAGEN_DELIVERY.NONE;
}

/** Etiqueta legible en admin. */
export function getNoticiaImagenOrigenLabel(row) {
  const delivery = resolveNoticiaImagenDelivery(row);
  if (delivery === NOTICIA_IMAGEN_DELIVERY.NONE) return null;

  const provider = resolveNoticiaImagenProvider(row?.metadata, row?.imagen_url);
  switch (provider) {
    case NOTICIA_IMAGEN_PROVIDER.PEXELS:
      return 'Pexels';
    case NOTICIA_IMAGEN_PROVIDER.POLLINATIONS:
      return 'Pollinations';
    case NOTICIA_IMAGEN_PROVIDER.MODELS_LAB:
      return 'Models Lab';
    case NOTICIA_IMAGEN_PROVIDER.HUGGINGFACE:
      return 'Hugging Face';
    case NOTICIA_IMAGEN_PROVIDER.OTHER:
      return 'Otra procedencia';
    default:
      return delivery === NOTICIA_IMAGEN_DELIVERY.BASE64 ? 'Imagen embebida' : 'DreamVinyl / IA';
  }
}
